// @djsem/shared — injectable identity + time primitives (OOP/DI refactor Phase A).
//
// Replaces the ~34 module-global `let _seq = 0` counters and hard-coded `AT` constants
// with injectable collaborators. Services accept these via *defaulted* constructor
// params, so existing zero-arg call-sites keep working unchanged while tests can inject
// deterministic, isolated instances.
//
// @see docs/state/agents/claude/prompts/102-repository-hardening-security-memory-oop-di-test-home.md

import { fnv1a } from "./ids";

/**
 * Injectable identity generator. Services accept an `IdFactory` via a defaulted
 * constructor param so production code needs no changes while tests can inject an
 * isolated instance for deterministic ids.
 */
export interface IdFactory {
  /**
   * Return a new id with the given prefix.
   * @param prefix - Id prefix, e.g. "air".
   * @returns A new id string, e.g. "air_1".
   */
  next(prefix: string): string;
}

/**
 * Injectable time source. Services accept a `Clock` via a defaulted constructor param
 * so production code defaults to real time while tests can inject a `FixedClock`.
 */
export interface Clock {
  /** @returns The current time as an ISO-8601 string. */
  now(): string;
}

/** Monotonic per-instance sequence. Fresh instance == fresh counter (test isolation). */
export class SeqIdFactory implements IdFactory {
  private n = 0;
  constructor(start = 0) { this.n = start; }
  next(prefix: string): string { return `${prefix}_${++this.n}`; }
}

/** Content-stable ids derived from prefix + counter via FNV-1a (non-cryptographic). */
export class FnvIdFactory implements IdFactory {
  private n = 0;
  next(prefix: string): string { return `${prefix}_${fnv1a(`${prefix}:${++this.n}`)}`; }
}

/** Deterministic clock for tests / reproducible artifacts. */
export class FixedClock implements Clock {
  constructor(private iso: string = "1970-01-01T00:00:00.000Z") {}
  now(): string { return this.iso; }
  set(iso: string): void { this.iso = iso; }
}

/** Real wall-clock time for production runtime. */
export class SystemClock implements Clock {
  now(): string { return new Date().toISOString(); }
}

/**
 * Shared default `IdFactory`. Services default to this so untouched callers need no
 * changes.
 *
 * NOTE: this is a process-shared singleton — the same footgun as the old module
 * globals. Prefer injecting a fresh `SeqIdFactory` in tests for isolation.
 */
export const defaultIds: IdFactory = new SeqIdFactory();

/**
 * Shared default `Clock`. Services default to this so untouched callers need no
 * changes.
 *
 * NOTE: this is a process-shared singleton — the same footgun as the old module
 * globals. Prefer injecting a fresh `FixedClock` in tests for isolation.
 */
export const defaultClock: Clock = new FixedClock();

/**
 * Minimal numeric counter used to replace module-global `let _seq = 0` mutable state.
 * `next()` returns the post-increment value (matching the old `++_seq` semantics);
 * `peek()` returns the current value without advancing (matching a bare `_seq` read).
 * `reset()` exists so tests can restore isolation deterministically.
 */
export class SeqCounter {
  private n: number;
  constructor(start = 0) { this.n = start; }
  next(): number { return ++this.n; }
  peek(): number { return this.n; }
  reset(to = 0): void { this.n = to; }
}
